import { Box, CircularProgress, Typography } from '@mui/material';
import React, { JSX, Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

// Layout & Protected Route — render synchronously around lazy routes
import Layout from './components/common/Layout/Layout';
import ProtectedRoute from './components/common/ProtectedRoute';
import RouteErrorBoundary from './components/common/RouteErrorBoundary';

import { authService } from './services/authService';
import { login, logout } from './store/slices/authSlice';
import { useAppDispatch, useAppSelector } from './store/store';
import { UserRole } from './types/common';

// Public / shared pages
const LandingPage = lazy(() => import('./pages/landing/LandingPage'));
const ForDoctorsPage = lazy(() => import('./pages/landing/for-doctors/ForDoctorsPage'));
const ApplyPage = lazy(() => import('./pages/apply/OnboardingWizard'));
const TrackingScreen = lazy(() => import('./pages/doctor-applications/TrackingScreen'));
const ResubmitForm = lazy(() => import('./pages/doctor-applications/ResubmitForm'));
const LoginPage = lazy(() => import('./pages/Login'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const ContactUs = lazy(() => import('./pages/ContactUs'));

// Doctor pages
const DoctorDashboard = lazy(() => import('./pages/doctor/DoctorDashboard'));
const Appointments = lazy(() => import('./pages/doctor/Appointments'));
const PatientList = lazy(() => import('./pages/doctor/PatientList'));
const PatientDetails = lazy(() => import('./pages/doctor/PatientDetails'));
const AvailabilitySettings = lazy(() => import('./pages/doctor/AvailabilitySettings'));
const ServiceConfiguration = lazy(() => import('./pages/doctor/ServiceConfiguration'));
const ClinicManagement = lazy(() => import('./pages/doctor/ClinicManagement'));
const DoctorProfile = lazy(() => import('./pages/doctor/DoctorProfile'));
const BookForPatient = lazy(() => import('./pages/doctor/BookForPatient'));
const DoctorBilling = lazy(() => import('./pages/doctor/DoctorBilling'));
const PaymentSettings = lazy(() => import('./pages/doctor/PaymentSettings'));
const PrescriptionWriter = lazy(() => import('./pages/doctor/PrescriptionWriter'));

// Patient pages
const PatientDashboard = lazy(() => import('./pages/patient/PatientDashboard'));
const PatientAppointments = lazy(() => import('./pages/patient/PatientAppointments'));
const PatientMedicalRecords = lazy(() => import('./pages/patient/PatientMedicalRecords'));
const PatientBilling = lazy(() => import('./pages/patient/PatientBilling'));
const PatientPrescriptions = lazy(() => import('./pages/patient/PatientPrescriptions'));
const PatientProfile = lazy(() => import('./pages/patient/PatientProfile'));
const BookAppointment = lazy(() => import('./pages/patient/BookAppointment'));
const AppointmentPayment = lazy(() => import('./pages/patient/AppointmentPayment'));

// Admin pages
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminDoctors = lazy(() => import('./pages/admin/AdminDoctors'));
const AdminDoctorDetail = lazy(() => import('./pages/admin/AdminDoctorDetail'));
const AdminDoctorApplications = lazy(
  () => import('./pages/admin/AdminDoctorApplications')
);
const AdminDoctorApplicationDetail = lazy(
  () => import('./pages/admin/AdminDoctorApplicationDetail')
);
const AdminPatients = lazy(() => import('./pages/admin/AdminPatients'));
const AdminPatientDetail = lazy(() => import('./pages/admin/AdminPatientDetail'));
const AdminAppointments = lazy(() => import('./pages/admin/AdminAppointments'));
const AdminJobs = lazy(() => import('./pages/admin/AdminJobs'));

const RouteFallback: React.FC = () => (
  <Box
    role="status"
    aria-label="Loading"
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

// Redirects already-logged-in users away from public-only pages (e.g. Login).
// Declared at module scope so it isn't recreated on every App render.
const PublicRoute = ({ children }: { children: JSX.Element }) => {
  const { token, user } = useAppSelector(state => state.auth);
  if (token && user) {
    if (user.role === UserRole.ADMIN) return <Navigate to="/admin/dashboard" replace />;
    if (user.role === UserRole.DOCTOR) return <Navigate to="/doctor/dashboard" replace />;
    return <Navigate to="/patient/dashboard" replace />;
  }
  return children;
};

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { token, user } = useAppSelector(state => state.auth);

  useEffect(() => {
    // Function to restore session
    const initAuth = async () => {
      // Case 1: We have a token (from LS) but no User data (Refresh happened)
      if (token && !user) {
        try {
          // Fetch the real user data from backend using the token
          const userProfile = await authService.getCurrentUser();

          // Update Redux with the fresh data
          dispatch(
            login({
              user: userProfile,
              accessToken: localStorage.getItem('auth_token') ?? token,
            })
          );
        } catch (error) {
          // If token is invalid/expired, force logout
          console.error('Session expired:', error);
          dispatch(logout());
        }
      }
    };

    initAuth();
  }, [token, user, dispatch]);

  if (!user && token) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <RouteErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* --- Landing Page (Public) --- */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/for-doctors" element={<ForDoctorsPage />} />
            <Route path="/apply" element={<ApplyPage />} />
            <Route
              path="/doctor-applications/track/:token"
              element={<TrackingScreen />}
            />
            <Route
              path="/doctor-applications/track/:token/resubmit"
              element={<ResubmitForm />}
            />

            {/* --- Public Routes (Guarded) --- */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/contact-us" element={<Navigate to="/contact" replace />} />

            {/* Protected Doctor Routes */}
            <Route path="/doctor" element={<ProtectedRoute allowedRoles={[UserRole.DOCTOR]} />}>
              <Route element={<Layout />}>
                <Route index element={<Navigate to="/doctor/dashboard" replace />} />
                <Route path="dashboard" element={<DoctorDashboard />} />
                <Route path="appointments" element={<Appointments />} />
                <Route path="appointments/:id" element={<Appointments />} />
                <Route path="patients" element={<PatientList />} />
                <Route path="patients/:id" element={<PatientDetails />} />
                <Route path="availability" element={<AvailabilitySettings />} />
                <Route path="services" element={<ServiceConfiguration />} />
                <Route path="clinics" element={<ClinicManagement />} />
                <Route path="profile" element={<DoctorProfile />} />
                <Route path="book-for-patient" element={<BookForPatient />} />
                <Route path="billing" element={<DoctorBilling />} />
                <Route path="payments" element={<PaymentSettings />} />
                <Route path="prescriptions/write/:appointmentId" element={<PrescriptionWriter />} />
              </Route>
            </Route>

            {/* Protected Patient Routes */}
            <Route path="/patient" element={<ProtectedRoute allowedRoles={[UserRole.PATIENT]} />}>
              {/* Patient Layout Routes */}
              <Route element={<Layout />}>
                <Route index element={<Navigate to="/patient/dashboard" replace />} />
                <Route path="dashboard" element={<PatientDashboard />} />
                <Route path="appointments" element={<PatientAppointments />} />
                <Route path="medical-records" element={<PatientMedicalRecords />} />
                <Route path="billing" element={<PatientBilling />} />
                <Route path="prescriptions" element={<PatientPrescriptions />} />
                <Route path="profile" element={<PatientProfile />} />
              </Route>

              {/* Standalone Patient Routes (without layout) */}
              <Route path="book-appointment" element={<BookAppointment />} />
              <Route path="payment/:appointmentId" element={<AppointmentPayment />} />
            </Route>

            {/* Personalized doctor booking link — doctor is locked and the doctor-selection
                step is skipped. Guarded as PATIENT so unauthenticated visitors are bounced
                to /login and returned here afterward (doctor preserved via the URL). */}
            <Route element={<ProtectedRoute allowedRoles={[UserRole.PATIENT]} />}>
              <Route path="/book/:doctorId" element={<BookAppointment />} />
            </Route>

            {/* Admin Login (standalone, no layout) */}
            <Route path="/admin/login" element={<AdminLogin />} />

            {/* Protected Admin Routes */}
            <Route path="/admin" element={<ProtectedRoute allowedRoles={[UserRole.ADMIN]} />}>
              <Route element={<Layout />}>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="doctors" element={<AdminDoctors />} />
                <Route path="doctors/:userId" element={<AdminDoctorDetail />} />
                <Route path="doctor-applications" element={<AdminDoctorApplications />} />
                <Route
                  path="doctor-applications/:id"
                  element={<AdminDoctorApplicationDetail />}
                />
                <Route path="patients" element={<AdminPatients />} />
                <Route path="patients/:userId" element={<AdminPatientDetail />} />
                <Route path="appointments" element={<AdminAppointments />} />
                <Route path="jobs" element={<AdminJobs />} />
              </Route>
            </Route>

            {/* Default Route — redirect unauthenticated users to landing page */}
            <Route
              path="*"
              element={
                token && user ? (
                  <Typography>404 Page not found.</Typography>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    </Box>
  );
};

export default App;
